import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import React, { useMemo } from 'react';
import cx from 'classnames';
import { selectBinaries, useAppSelector } from '@parca/store';
import { isSearchMatch, scaleLinear } from '@parca/utilities';
import 'react-contexify/dist/ReactContexify.css';
import { FIELD_CHILDREN, FIELD_CUMULATIVE, FIELD_DIFF, FIELD_FUNCTION_NAME, FIELD_MAPPING_FILE, } from './index';
import useNodeColor from './useNodeColor';
import { arrowToString, nodeLabel } from './utils';
export const RowHeight = 26;
export const IcicleGraphNodes = React.memo(function IcicleGraphNodesNoMemo({ table, childRows, mappingColors, x, y, xScale, total, totalWidth, level, path, setCurPath, setHoveringRow, setHoveringLevel, curPath, sortBy, searchString, darkMode, compareMode, isContextMenuOpen, hoveringName, setHoveringName, hoveringRow, colorForSimilarNodes, highlightSimilarStacksPreference, }) {
    const cumulatives = table.getChild(FIELD_CUMULATIVE);
    if (childRows === undefined || childRows.length === 0) {
        return _jsx(_Fragment, {});
    }
    childRows =
        curPath.length === 0
            ? childRows
            : childRows.filter(c => nodeLabel(table, c, level, false) === curPath[0]);
    let childrenCumulative = BigInt(0);
    const childrenElements = [];
    childRows.forEach((child, i) => {
        const xStart = Math.floor(xScale(BigInt(childrenCumulative)));
        const c = BigInt(cumulatives?.get(child));
        childrenCumulative += c;
        childrenElements.push(_jsx(IcicleNode, { table: table, row: child, mappingColors: mappingColors, x: xStart, y: 0, totalWidth: totalWidth, height: RowHeight, path: path, setCurPath: setCurPath, setHoveringRow: setHoveringRow, setHoveringLevel: setHoveringLevel, level: level, curPath: curPath, total: total, xScale: xScale, sortBy: sortBy, searchString: searchString, darkMode: darkMode, compareMode: compareMode, isContextMenuOpen: isContextMenuOpen, hoveringName: hoveringName, setHoveringName: setHoveringName, hoveringRow: hoveringRow, colorForSimilarNodes: colorForSimilarNodes, highlightSimilarStacksPreference: highlightSimilarStacksPreference }, `node-${level}-${i}`));
    });
    return _jsx("g", { transform: `translate(${x}, ${y})`, children: childrenElements });
});
const icicleRectStyles = {
    cursor: 'pointer',
    transition: 'opacity .15s linear',
};
const fadedIcicleRectStyles = {
    cursor: 'pointer',
    transition: 'opacity .15s linear',
    opacity: '0.5',
};
export const IcicleNode = React.memo(function IcicleNodeNoMemo({ table, row, mappingColors, x, y, height, setCurPath, curPath, level, path, total, totalWidth, xScale, isRoot = false, searchString, setHoveringRow, setHoveringLevel, sortBy, darkMode, compareMode, isContextMenuOpen, hoveringName, setHoveringName, hoveringRow, colorForSimilarNodes, highlightSimilarStacksPreference, }) {
    // get the columns to read from
    const mappingColumn = table.getChild(FIELD_MAPPING_FILE);
    const functionNameColumn = table.getChild(FIELD_FUNCTION_NAME);
    const cumulativeColumn = table.getChild(FIELD_CUMULATIVE);
    const diffColumn = table.getChild(FIELD_DIFF);
    // get the actual values from the columns
    const mappingFile = arrowToString(mappingColumn?.get(row));
    const functionName = arrowToString(functionNameColumn?.get(row));
    const cumulative = cumulativeColumn?.get(row) !== null ? BigInt(cumulativeColumn?.get(row)) : 0n;
    const diff = diffColumn?.get(row) !== null ? BigInt(diffColumn?.get(row)) : null;
    const childRows = Array.from(table.getChild(FIELD_CHILDREN)?.get(row) ?? []);
    const highlightedNodes = useMemo(() => {
        if (!highlightSimilarStacksPreference) {
            return null;
        }
        if (functionName != null && functionName === hoveringName) {
            return { functionName, row: hoveringRow };
        }
        return null; // Nothing to highlight
    }, [functionName, hoveringName, hoveringRow, highlightSimilarStacksPreference]);
    const shouldBeHighlightedIfSimilarStacks = useMemo(() => {
        return highlightedNodes !== null && row !== highlightedNodes.row;
    }, [row, highlightedNodes]);
    // TODO: Maybe it's better to pass down the sorter function as prop instead of figuring this out here.
    switch (sortBy) {
        case FIELD_FUNCTION_NAME:
            childRows.sort((a, b) => {
                // TODO: Support fallthrough to comparing addresses or something
                const afn = arrowToString(functionNameColumn?.get(a));
                const bfn = arrowToString(functionNameColumn?.get(b));
                if (afn !== null && bfn !== null) {
                    return afn.localeCompare(bfn);
                }
                if (afn === null && bfn !== null) {
                    return -1;
                }
                if (afn !== null && bfn === null) {
                    return 1;
                }
                // both are null
                return 0;
            });
            break;
        case FIELD_CUMULATIVE:
            childRows.sort((a, b) => {
                const aCumulative = cumulativeColumn?.get(a);
                const bCumulative = cumulativeColumn?.get(b);
                return Number(bCumulative - aCumulative);
            });
            break;
        case FIELD_DIFF:
            childRows.sort((a, b) => {
                const aDiff = diffColumn?.get(a);
                const bDiff = diffColumn?.get(b);
                if (aDiff !== null && bDiff !== null) {
                    return Number(bDiff - aDiff);
                }
                if (aDiff === null && bDiff !== null) {
                    return -1;
                }
                if (aDiff !== null && bDiff === null) {
                    return 1;
                }
                // both are null
                return 0;
            });
            break;
    }
    const binaries = useAppSelector(selectBinaries);
    const colorResult = useNodeColor({
        isDarkMode: darkMode,
        compareMode,
        cumulative,
        diff,
        mappingColors,
        mappingFile,
    });
    const name = useMemo(() => {
        return isRoot ? 'root' : nodeLabel(table, row, level, binaries.length > 1);
    }, [table, row, level, isRoot, binaries]);
    const nextPath = path.concat([name]);
    const isFaded = curPath.length > 0 && name !== curPath[curPath.length - 1];
    const styles = isFaded ? fadedIcicleRectStyles : icicleRectStyles;
    const nextLevel = level + 1;
    const nextCurPath = curPath.length === 0 ? [] : curPath.slice(1);
    const newXScale = nextCurPath.length === 0 && curPath.length === 1
        ? scaleLinear([0n, BigInt(cumulative)], [0, totalWidth])
        : xScale;
    const width = nextCurPath.length > 0 || (nextCurPath.length === 0 && curPath.length === 1)
        ? totalWidth
        : xScale(BigInt(cumulative));
    const { isHighlightEnabled = false, isHighlighted = false } = useMemo(() => {
        if (searchString === undefined || searchString === '') {
            return { isHighlightEnabled: false };
        }
        return { isHighlightEnabled: true, isHighlighted: isSearchMatch(searchString, name) };
    }, [searchString, name]);
    if (width <= 1) {
        return _jsx(_Fragment, { children: null });
    }
    const onMouseEnter = () => {
        if (isContextMenuOpen)
            return;
        setHoveringRow(row);
        setHoveringLevel(level);
        setHoveringName(name);
    };
    const onMouseLeave = () => {
        if (isContextMenuOpen)
            return;
        setHoveringRow(null);
        setHoveringLevel(null);
        setHoveringName(null);
    };
    return (_jsxs(_Fragment, { children: [_jsxs("g", { transform: `translate(${x + 1}, ${y + 1})`, style: styles, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onClick: () => {
                    setCurPath(nextPath);
                }, children: [_jsx("rect", { x: 0, y: 0, width: width, height: height, style: {
                            fill: colorResult,
                        }, className: cx(shouldBeHighlightedIfSimilarStacks
                            ? `${colorForSimilarNodes} stroke-[3] [stroke-dasharray:6,4] [stroke-linecap:round] [stroke-linejoin:round] h-6`
                            : 'stroke-white dark:stroke-gray-700', {
                            'opacity-50': isHighlightEnabled && !isHighlighted,
                        }) }), width > 5 && (_jsx("svg", { width: width - 5, height: height, children: _jsx("text", { x: 5, y: 15, style: { fontSize: '12px' }, children: name }) }))] }), childRows.length > 0 && (_jsx(IcicleGraphNodes, { table: table, row: row, mappingColors: mappingColors, childRows: childRows, x: x, y: RowHeight, xScale: newXScale, total: total, totalWidth: totalWidth, level: nextLevel, path: nextPath, curPath: nextCurPath, setCurPath: setCurPath, setHoveringRow: setHoveringRow, setHoveringLevel: setHoveringLevel, searchString: searchString, sortBy: sortBy, darkMode: darkMode, compareMode: compareMode, isContextMenuOpen: isContextMenuOpen, hoveringName: hoveringName, setHoveringName: setHoveringName, hoveringRow: hoveringRow, colorForSimilarNodes: colorForSimilarNodes, highlightSimilarStacksPreference: highlightSimilarStacksPreference }))] }));
});
